import type { AnyTokenSymbol, Chain, NativeSymbol, TokenSymbol } from './types';

export const SUPPORTED_CHAINS = [
  'eth',
  'trx',
  'bsc',
  'sol',
  'hyperevm',
  'ton',
  'btc',
  'zec',
  'xmr',
  'doge',
  'xrp',
] as const;
/** Stablecoin (contract) tokens ONLY. Iterated by ERC-20/TRC-20 code paths —
 *  native coins must never join this list (they have no contract/balanceOf). */
export const SUPPORTED_TOKENS = ['USDT', 'USDC'] as const;
/** Native chain coins — parallel registry, see NATIVES for decimals/chain. */
export const NATIVE_TOKENS = [
  'ETH',
  'BTC',
  'TRX',
  'BNB',
  'SOL',
  'HYPE',
  'TON',
  'ZEC',
  'XMR',
  'DOGE',
  'XRP',
] as const;
export const ALL_TOKENS = [...SUPPORTED_TOKENS, ...NATIVE_TOKENS] as const;
export const SEED_BACKUP_METHODS = ['drive', 'tg', 'local'] as const;
export const SUPPORTED_LANGUAGES = [
  'en',
  'ru',
  'uk',
  'pl',
  'es',
  'tr',
  'el',
  'it',
  'de',
  'pt',
] as const;

/** True for locales that should get Cyrillic bot/copy (UK falls back to RU). */
export function prefersCyrillic(lang?: string | null): boolean {
  return lang === 'ru' || lang === 'uk';
}
/** Flat USD fee per outgoing transfer (admin-configurable; static for now). */
export const TRANSFER_FEE_USD = 1.5;

/**
 * Partner margin on ChangeHero swaps (percent of from-amount). When this USD
 * margin covers the sponsor cost of a 1-leg deposit, the network fee is waived
 * for the user (single-leg send, no treasury fee).
 *
 * 0 = no affiliate cut — never subsidize deposit gas from "virtual" margin;
 * Free only when the 1-leg sponsor cost is already $0 (leftover energy).
 */
export const SWAP_NETWORK_SUBSIDY_PCT = 0;

/** USD margin from a swap from-amount at {@link SWAP_NETWORK_SUBSIDY_PCT}. */
export function swapNetworkMarginUsd(amountUsd: number): number {
  if (!(amountUsd > 0) || !Number.isFinite(amountUsd)) return 0;
  return (amountUsd * SWAP_NETWORK_SUBSIDY_PCT) / 100;
}

/** True when swap partner margin covers the 1-leg sponsor cost.
 *  Cost 0 (leftover energy / nothing to rent) is always covered. */
export function swapCoversNetworkFee(amountUsd: number, oneLegCostUsd: number): boolean {
  if (!Number.isFinite(oneLegCostUsd) || oneLegCostUsd < 0) return false;
  if (oneLegCostUsd === 0) return amountUsd > 0 && Number.isFinite(amountUsd);
  return swapNetworkMarginUsd(amountUsd) + 1e-9 >= oneLegCostUsd;
}

/**
 * Flat USD fee the user pays to issue a card — a fixed price, NOT the provider's
 * apply fee. The project pays the provider's apply fee from its Banana balance;
 * profit = this fee − apply fee. Admin-configurable via `CARD_ISSUE_FEE_KEY`.
 */
export const CARD_ISSUE_FEE_USD = 50;
export const CARD_ISSUE_FEE_KEY = 'card.issue_fee_usd';

/** USDT price of one AML check. Admin override: `AML_UNIT_PRICE_KEY`. */
export const AML_UNIT_PRICE_USD = 0.5;
export const AML_UNIT_PRICE_KEY = 'aml.unit_price_usd';
/** Set false in app_config to stop new purchases and checks. */
export const AML_ENABLED_KEY = 'aml.enabled';
/** Optional TRON treasury for AML purchases. Falls back to the fee treasury. */
export const AML_TREASURY_KEY = 'aml.treasury_trx';

/**
 * Flat % the user pays on every card top-up — same for all cards, NOT the
 * provider's `recharge_fee`. Banana is paid from float; margin =
 * this % − provider %. Min USD still falls back when the template omits it.
 */
export const CARD_RECHARGE_FEE_PCT = 2.5;
export const CARD_RECHARGE_MIN_USD = 20;

/**
 * Flat % taken from every card → USDT withdrawal. Card is debited the full
 * request amount; the user receives `amount × (1 − this % / 100)`.
 */
export const CARD_WITHDRAW_FEE_PCT = 3;

/** Net USDT credited to the user after {@link CARD_WITHDRAW_FEE_PCT} (2 dp). */
export function cardWithdrawPayoutUsd(cardAmountUsd: number): number {
  if (!(cardAmountUsd > 0)) return 0;
  return Math.round(cardAmountUsd * (1 - CARD_WITHDRAW_FEE_PCT / 100) * 100) / 100;
}

/**
 * Banana's own top-up cut (`recharge_fee` on the card template) — paid out of
 * our float on every recharge, so our slice is CARD_RECHARGE_FEE_PCT minus it.
 * Only a fallback: the effective value is read from the live (cached) template.
 */
export const BANANA_RECHARGE_FEE_PCT = 2.1;

/**
 * Provider recharge % from a template payload, capped at
 * {@link BANANA_RECHARGE_FEE_PCT}.
 *
 * Banana's cut is at most 2.1% — anything higher is a misread (renamed field,
 * basis points, garbage). Reading it too HIGH would shrink the discount; reading
 * a nonsense zero would inflate it. A real percentage in (0, 2.1] is taken as-is;
 * anything else falls back to the known maximum.
 */
export function bananaRechargeFeePct(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0 || n >= 100) return BANANA_RECHARGE_FEE_PCT;
  return Math.min(n, BANANA_RECHARGE_FEE_PCT);
}

/**
 * Fixed spend limits we advertise for all cards (USDT). Same values at issue
 * and in card settings — independent of the Banana template payload.
 */
export const CARD_LIMITS = {
  perTx: 20_000,
  perDay: 250_000,
  perMonth: 1_000_000,
} as const;

/**
 * Nationalities / residencies where Banana KYC rejects cardholders
 * ("Restricted nationality"). Shown in the TMA as prohibited countries.
 * Update when the issuer list changes.
 */
export const KYC_RESTRICTED_COUNTRIES = [
  'Afghanistan',
  'Belarus',
  'Central African Republic',
  'Cuba',
  'Democratic Republic of the Congo',
  'Iran',
  'Iraq',
  'Lebanon',
  'Libya',
  'Mali',
  'Myanmar',
  'Nicaragua',
  'North Korea',
  'Russia',
  'Somalia',
  'South Sudan',
  'Sudan',
  'Syria',
  'Venezuela',
  'Yemen',
  'Zimbabwe',
] as const;

/** Always {@link CARD_RECHARGE_FEE_PCT}. Args kept so existing call sites compile. */
export function cardRechargeFeePct(_value?: unknown, _templateId?: number | null): number {
  return CARD_RECHARGE_FEE_PCT;
}

/** Positive finite min USD, else {@link CARD_RECHARGE_MIN_USD}. */
export function cardRechargeMinUsd(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : CARD_RECHARGE_MIN_USD;
}

/** Positive finite spend limit, else the matching {@link CARD_LIMITS} entry. */
export function cardSpendLimit(value: unknown, key: keyof typeof CARD_LIMITS): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : CARD_LIMITS[key];
}

/** `app_config` key: URL to which every incoming Banana webhook is relayed
 *  (the integrator's original bot). Empty = no forwarding. */
export const BANANA_WEBHOOK_FORWARD_KEY = 'banana.webhook_forward_url';

/**
 * Abuse-protection limits for outgoing transfers. These are the built-in
 * defaults; each is overridable at runtime from the admin panel via the
 * matching `app_config` key (see TRANSFER_LIMIT_KEYS).
 */
export interface TransferLimits {
  /** Smallest allowed transfer amount (token units, ≈ USD). */
  minAmount: number;
  /** Max number of outgoing transfers per rolling 24h. */
  dailyTxLimit: number;
  /** Minimum seconds between two consecutive outgoing transfers. */
  cooldownSeconds: number;
  /** Flat USD fee charged per outgoing transfer. */
  feeUsd: number;
}

export const DEFAULT_TRANSFER_LIMITS: TransferLimits = {
  minAmount: 1,
  dailyTxLimit: 50,
  cooldownSeconds: 5,
  feeUsd: TRANSFER_FEE_USD,
};

/** `app_config` keys the admin panel writes to override the defaults above. */
export const TRANSFER_LIMIT_KEYS = {
  minAmount: 'transfer.min_amount',
  dailyTxLimit: 'transfer.daily_tx_limit',
  cooldownSeconds: 'transfer.cooldown_seconds',
  feeUsd: 'transfer.fee_usd',
} as const;

/** Abuse limits for automated card → USDT TRC-20 withdrawals. */
export interface CardWithdrawLimits {
  /** Smallest allowed withdrawal (USD ≈ USDT). */
  minAmountUsd: number;
  /** Largest allowed withdrawal per request (USD ≈ USDT). */
  maxAmountUsd: number;
  /** Max withdrawal requests per user in a rolling 1h window. */
  maxPerHour: number;
}

export const DEFAULT_CARD_WITHDRAW_LIMITS: CardWithdrawLimits = {
  minAmountUsd: 10,
  maxAmountUsd: 500,
  maxPerHour: 3,
};

export const CARD_WITHDRAW_LIMIT_KEYS = {
  minAmountUsd: 'card.withdraw.min_amount_usd',
  maxAmountUsd: 'card.withdraw.max_amount_usd',
  maxPerHour: 'card.withdraw.max_per_hour',
} as const;

/** Auto Banana+re-energy payout after a user request. Missing/false = off. */
export const CARD_WITHDRAW_AUTO_KEY = 'card.withdraw.auto_enabled';
export const DEFAULT_CARD_WITHDRAW_AUTO = false;

/**
 * Fee model. `flat` charges the static `transfer.fee_usd` (legacy behaviour).
 * `cost_plus` charges the estimated sponsorship cost (gas/energy) × (1 + markup),
 * floored at `fee_min_usd` — so the project recovers its real cost plus a margin
 * instead of a fixed fee that loses money on pricey ETH and overcharges on Tron.
 */
export type FeeMode = 'flat' | 'cost_plus';

/**
 * Cost-plus fee settings (admin-configurable via the matching `app_config` keys).
 *
 * Principle: user fee MUST exceed what we pay to sponsor the send (refee / Alchemy),
 * otherwise every transfer is a cash gap. Tron cost is EXACT (refee quote). ETH
 * cost is an estimate (Alchemy bill lands post-mine) — padded gas units + markup
 * + flatUsd floor absorb underestimation. Falls back to flatUsd if pricing fails.
 */
export interface FeeSettings {
  mode: FeeMode;
  /** Profit margin over cost, percent (20 = cost × 1.20). */
  markupPct: number;
  /** Minimum fee floor in USD (mainly Tron) — the fee never drops below this. */
  minUsd: number;
  /** Flat USD fee used in `flat` mode, ETH floor, and estimation-failure fallback. */
  flatUsd: number;
  /**
   * Assumed gas units for an ETH sponsored UserOp (7702 + 2×ERC-20). Priced as
   * gasUnits × live gasPrice × ETH/USD, then padded during fee pricing before
   * markup — keep this ≥ real Alchemy usage or we undercharge.
   */
  ethGasUnits: number;
}

export const DEFAULT_FEE_SETTINGS: FeeSettings = {
  mode: 'cost_plus',
  markupPct: 20,
  minUsd: 0.5, // Tron floor — ETH also floored at flatUsd
  flatUsd: TRANSFER_FEE_USD,
  // 7702 ModularAccount + recipient transfer + treasury-fee transfer (+ headroom).
  ethGasUnits: 600_000,
};

/** `app_config` keys the admin panel writes to override the fee settings above. */
export const FEE_KEYS = {
  mode: 'transfer.fee_mode',
  markupPct: 'transfer.fee_markup_pct',
  minUsd: 'transfer.fee_min_usd',
  ethGasUnits: 'fee.eth_gas_units',
} as const;

/**
 * How much of the blockchain fee the project hides from the user on a card
 * payment.
 *
 * Issuance is fully absorbed — the flat issue fee already clears the provider's
 * apply fee by a wide margin, so charging network fee on top only deters the
 * purchase. A top-up may only spend the part of OUR slice (product % − Banana %)
 * that survives `topupKeepPct`, so no single top-up can settle below that
 * margin no matter how expensive the chain gets.
 */
export interface CardFeePolicy {
  /** Times the project pays a wallet's warm-up (activation + disperse approve)
   *  on a TOP-UP. Issuance warms for free and never consumes this quota. */
  warmMaxPerUser: number;
  /** Issue payments: charge the user $0 network fee. */
  issueAbsorbTransfer: boolean;
  /** Margin kept on every top-up before any network subsidy, % of the amount. */
  topupKeepPct: number;
  /** Kill switch for the automatic network-fee discount on top-ups. */
  topupSubsidyEnabled: boolean;
}

/**
 * Ceiling on the wallet warm-up gifted on ONE top-up.
 *
 * A warm-up is ~$3, but the sponsor debt behind it ACCUMULATES: every abandoned
 * first send rents its approve energy at our expense and the debt is only
 * cleared by a successful send. Without a ceiling, a wallet that started and
 * dropped ten sends could forgive the whole bill with one $20 top-up. Debt above
 * this stays on the user's side of the ledger and is recovered as usual.
 */
export const WARM_GIFT_MAX_USD = 6;

export const DEFAULT_CARD_FEE_POLICY: CardFeePolicy = {
  warmMaxPerUser: 2,
  issueAbsorbTransfer: true,
  topupKeepPct: 0.25,
  topupSubsidyEnabled: true,
};

/** `app_config` keys the admin panel writes to override the policy above. */
export const CARD_FEE_POLICY_KEYS = {
  warmMaxPerUser: 'sponsor.warm_max_per_user',
  issueAbsorbTransfer: 'card.issue_absorb_transfer',
  topupKeepPct: 'card.topup_keep_pct',
  topupSubsidyEnabled: 'card.topup_subsidy_enabled',
} as const;

export interface TopupSubsidyInput {
  /** Top-up base amount in USD (what the card gets credited). */
  amountUsd: number;
  /** What we charge the user on top of the amount (CARD_RECHARGE_FEE_PCT). */
  productFeePct: number;
  /** Banana's cut of the same top-up (`recharge_fee`). */
  providerFeePct: number;
  keepPct: number;
  /** Cost-plus network fee before any discount. */
  fullFeeUsd: number;
  enabled: boolean;
}

export interface TopupSubsidy {
  /** % of the amount available to pay down the network fee. 0 = no discount. */
  subsidyPct: number;
  subsidyBudgetUsd: number;
  /** Network fee the user actually pays. */
  chargedFeeUsd: number;
  /** Network fee the project eats out of its slice. */
  subsidizedUsd: number;
  /** Top-up amount from which the network fee is free — null when there is no
   *  budget to give (Banana takes our whole slice, or the switch is off). */
  freeFromUsd: number | null;
  /** Top-up amount from which roughly half the network fee is covered. */
  halfFromUsd: number | null;
}

/**
 * Network fee left for the user after `subsidyPct` of the top-up amount is spent
 * on it. Exported on its own so the TMA can re-derive the fee for the amount
 * being typed without another quote, off the same arithmetic the API charges by.
 */
export function subsidizedNetworkFee(
  fullFeeUsd: number,
  amountUsd: number,
  subsidyPct: number,
): number {
  const budgetUsd = (Math.max(0, amountUsd) * Math.max(0, subsidyPct)) / 100;
  return Math.round(Math.max(0, Math.max(0, fullFeeUsd) - budgetUsd) * 100) / 100;
}

/**
 * The whole top-up fee policy in one place: how much of `fullFeeUsd` the user
 * pays, and the amounts at which the discount becomes half / full. Pure, so the
 * API charge and the TMA hint can never disagree.
 */
export function topupSubsidy(i: TopupSubsidyInput): TopupSubsidy {
  const subsidyPct = i.enabled
    ? Math.max(0, i.productFeePct - i.providerFeePct - Math.max(0, i.keepPct))
    : 0;
  const budgetUsd = (Math.max(0, i.amountUsd) * subsidyPct) / 100;
  const fullFeeUsd = Math.max(0, i.fullFeeUsd);
  const chargedFeeUsd = subsidizedNetworkFee(fullFeeUsd, i.amountUsd, subsidyPct);
  // Whole dollars: these are shown to the user and offered as a prefill, and a
  // rounded-DOWN threshold would promise a free fee the budget cannot cover.
  const freeFromUsd =
    subsidyPct > 0 && fullFeeUsd > 0 ? Math.ceil(fullFeeUsd / (subsidyPct / 100)) : null;
  return {
    subsidyPct,
    subsidyBudgetUsd: budgetUsd,
    chargedFeeUsd,
    subsidizedUsd: Math.round((fullFeeUsd - chargedFeeUsd) * 100) / 100,
    freeFromUsd,
    halfFromUsd: freeFromUsd === null ? null : Math.ceil(freeFromUsd / 2),
  };
}

/** `app_config` boolean key — when true, the API rejects all new transfers. */
export const KILL_SWITCH_KEY = 'kill_switch';

/**
 * Treasury wallet addresses (where transfer / card fees are collected).
 * Admin-configurable; env `TREASURY_*` / `CARD_FEE_TREASURY_*` remain fallbacks
 * when the matching `app_config` row is unset/blank.
 */
export const TREASURY_KEYS = {
  eth: 'treasury.eth',
  trx: 'treasury.trx',
  /** BNB Smart Chain shares Ethereum's address space, so this defaults to the
   *  `eth` wallet — the same address, reached on the other chain. Set it only
   *  to route BSC fees somewhere of their own. */
  bsc: 'treasury.bsc',
  /** Solana is its own address space, so this cannot fall back to another
   *  chain's wallet — a sponsored SPL send has nowhere to settle until it is
   *  set, and the route refuses rather than sending for free. */
  sol: 'treasury.sol',
  /** HyperEVM shares Ethereum's address space like BSC does, so this defaults
   *  to the `eth` wallet. Nothing settles here while sends are self-paid —
   *  a self-paid send charges no service fee — but the slot exists so the
   *  capability test does not have to special-case the chain. */
  hyperevm: 'treasury.hyperevm',
  /** TON is its own address space, so like Solana this cannot fall back to
   *  another chain's wallet. Nothing settles here while sends are self-paid. */
  ton: 'treasury.ton',
  cardFeeEth: 'treasury.card_fee_eth',
  cardFeeTrx: 'treasury.card_fee_trx',
  cardFeeBsc: 'treasury.card_fee_bsc',
  cardFeeSol: 'treasury.card_fee_sol',
  cardFeeHyperevm: 'treasury.card_fee_hyperevm',
  cardFeeTon: 'treasury.card_fee_ton',
  /** USDT TRC-20 destination for INCPT presale payments. */
  presaleTrx: 'treasury.presale_trx',
  /** USDT TRC-20 destination for staking deposits. */
  stakingTrx: 'treasury.staking_trx',
} as const;

/** Current INCPT presale round terms (admin-editable; no stage table). */
/**
 * `app_config` keys for staking. The daily rate is drawn once a day from the
 * [min, max] band below and shared by every position accruing that day, so the
 * admin controls the yield with two numbers and nothing else.
 */
export const STAKING_KEYS = {
  open: 'staking.open',
  dailyRateMinPct: 'staking.daily_rate_min_pct',
  dailyRateMaxPct: 'staking.daily_rate_max_pct',
  minAmountUsd: 'staking.min_amount_usd',
  maxAmountUsd: 'staking.max_amount_usd',
  maxOpenPositions: 'staking.max_open_positions',
  /** Flat USDT fee on a withdrawal to an address outside the wallet. */
  externalWithdrawFeeUsd: 'staking.external_withdraw_fee_usd',
  /** Flat USDT fee on a withdrawal back to the user's own wallet. */
  walletWithdrawFeeUsd: 'staking.wallet_withdraw_fee_usd',
  /** TG usernames / numeric ids allowed while `open` is false. */
  allowlist: 'staking.allowlist',
  /** Days between profit withdrawals on a `profit` vault. */
  profitWithdrawEveryDays: 'staking.profit_withdraw_every_days',
  /**
   * Hours between accrual credits per deposit tranche (default 24).
   * Shorter values are for testing; the credited amount is scaled by hours/24
   * so advertised APY stays honest.
   */
  accrualEveryHours: 'staking.accrual_every_hours',
} as const;

export const STAKING_VAULT_KINDS = ['liquid', 'monthly', 'maximum'] as const;
export type StakingVaultKind = (typeof STAKING_VAULT_KINDS)[number];

/**
 * `app_config` keys for one vault. Each vault carries its own rate band, cap
 * and term, so opening Monthly never means inheriting Liquid's numbers.
 *
 * Liquid falls back to the flat `staking.*` keys when its own are unset, so the
 * values an admin already entered keep working without being re-typed.
 */
export const stakingVaultKeys = (kind: StakingVaultKind) =>
  ({
    open: `staking.${kind}.open`,
    dailyRateMinPct: `staking.${kind}.daily_rate_min_pct`,
    dailyRateMaxPct: `staking.${kind}.daily_rate_max_pct`,
    maxAmountUsd: `staking.${kind}.max_amount_usd`,
    termDays: `staking.${kind}.term_days`,
    maxOpenPositions: `staking.${kind}.max_open_positions`,
  }) as const;

export interface StakingVaultConfig {
  /** Accepts new deposits. The product-level `open` gate still applies. */
  open: boolean;
  dailyRateMinPct: number;
  dailyRateMaxPct: number;
  /** Cap across every open position in this vault, per user. */
  maxAmountUsd: number;
  /** How long the principal is committed for. Ignored by Liquid. */
  termDays: number;
  /** Open positions per user; null = unlimited. */
  maxOpenPositions: number | null;
}

export interface StakingConfig {
  /** Product gate: when false, only {@link allowlist} users see staking at all
   *  (same gate as presale). Individual vaults have their own `open` on top. */
  open: boolean;
  minAmountUsd: number;
  externalWithdrawFeeUsd: number;
  walletWithdrawFeeUsd: number;
  allowlist: string[];
  legal: { terms: string; aml: string; privacy: string };
  vaults: Record<StakingVaultKind, StakingVaultConfig>;
  /** How often a `profit` vault releases its accrued profit, in days. */
  profitWithdrawEveryDays: number;
  /** Hours between accrual credits per deposit (default 24). */
  accrualEveryHours: number;
}

/**
 * Built-in defaults. The bands are the prototype's advertised APYs converted to
 * a daily rate — 20–40% for Liquid, 50–70% for Monthly, 80–100% for Maximum —
 * so the screen reads the same as the design before an admin touches anything.
 */
export const DEFAULT_STAKING_VAULTS: Record<StakingVaultKind, StakingVaultConfig> = {
  liquid: {
    open: true,
    dailyRateMinPct: 0.05,
    dailyRateMaxPct: 0.092,
    maxAmountUsd: 20_000,
    termDays: 365,
    maxOpenPositions: 1,
  },
  monthly: {
    open: false,
    dailyRateMinPct: 0.111,
    dailyRateMaxPct: 0.145,
    maxAmountUsd: 50_000,
    termDays: 365,
    maxOpenPositions: null,
  },
  maximum: {
    open: false,
    dailyRateMinPct: 0.161,
    dailyRateMaxPct: 0.19,
    maxAmountUsd: 50_000,
    termDays: 365,
    maxOpenPositions: null,
  },
};

/** Profit on a `profit` vault is released once a month. */
export const DEFAULT_PROFIT_WITHDRAW_DAYS = 30;

/** Production accrual cadence: one credit every 24 hours per deposit tranche. */
export const DEFAULT_ACCRUAL_EVERY_HOURS = 24;

export const DEFAULT_STAKING: StakingConfig = {
  open: false,
  minAmountUsd: 10,
  externalWithdrawFeeUsd: 1,
  walletWithdrawFeeUsd: 0,
  allowlist: [],
  legal: {
    terms: 'https://incpt.io/terms/',
    aml: 'https://incpt.io/aml/',
    privacy: 'https://incpt.io/privacy/',
  },
  vaults: DEFAULT_STAKING_VAULTS,
  profitWithdrawEveryDays: DEFAULT_PROFIT_WITHDRAW_DAYS,
  accrualEveryHours: DEFAULT_ACCRUAL_EVERY_HOURS,
};

/**
 * Hard ceiling on the admin's daily-rate band, percent. A typo of 5 instead of
 * 0.5 would otherwise promise a 6-figure APY; the band is clamped here so the
 * worst an admin can do is cap out, not break the product.
 */
export const MAX_DAILY_RATE_PCT = 1;

/** Effective APY for a daily rate, percent — daily compounding over 365 days. */
export function apyFromDailyPct(dailyPct: number): number {
  return (Math.pow(1 + dailyPct / 100, 365) - 1) * 100;
}

export const PRESALE_KEYS = {
  open: 'presale.open',
  priceUsd: 'presale.price_usd',
  minAmountUsd: 'presale.min_amount_usd',
  marketPriceUsd: 'presale.market_price_usd',
  marketChange24h: 'presale.market_change_24h',
  /** TG usernames (no @) allowed while `open` is false. */
  allowlist: 'presale.allowlist',
} as const;

export interface PresaleRoundConfig {
  open: boolean;
  priceUsd: number;
  minAmountUsd: number;
  marketPriceUsd: number;
  marketChange24h: number;
  allowlist: string[];
}

export const DEFAULT_PRESALE: PresaleRoundConfig = {
  open: false,
  priceUsd: 0.3,
  minAmountUsd: 100,
  marketPriceUsd: 0.4,
  marketChange24h: 25,
  allowlist: [],
};

/** `app_config` keys for referral commission on lockup staking deposits. */
export const REFERRAL_KEYS = {
  enabled: 'referral.enabled',
  rewardPct: 'referral.reward_pct',
  /** JSON array of vault kinds that pay commission, e.g. `["monthly","maximum"]`. */
  vaults: 'referral.vaults',
} as const;

export type ReferralRewardMode = 'incpt' | 'liquid';

export interface ReferralConfig {
  enabled: boolean;
  rewardPct: number;
  vaults: StakingVaultKind[];
}

export const DEFAULT_REFERRAL: ReferralConfig = {
  enabled: true,
  rewardPct: 5,
  vaults: ['monthly', 'maximum'],
};

/** Parse admin-configured vault list; unknown kinds are dropped. */
export function parseReferralVaults(raw: unknown): StakingVaultKind[] {
  const allowed = new Set<string>(STAKING_VAULT_KINDS);
  const parts: string[] = [];
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (typeof item === 'string') parts.push(item);
    }
  } else if (typeof raw === 'string') {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        for (const item of parsed) {
          if (typeof item === 'string') parts.push(item);
        }
      } else {
        parts.push(...raw.split(/[\s,;]+/));
      }
    } catch {
      parts.push(...raw.split(/[\s,;]+/));
    }
  }
  const out: StakingVaultKind[] = [];
  const seen = new Set<string>();
  for (const p of parts) {
    const k = p.trim().toLowerCase();
    if (!allowed.has(k) || seen.has(k)) continue;
    seen.add(k);
    out.push(k as StakingVaultKind);
  }
  return out;
}

/** Normalize a Telegram username for allowlist matching (strip @, lower-case). */
export function normalizePresaleUsername(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const u = raw.trim().replace(/^@+/, '').toLowerCase();
  return u.length > 0 ? u : null;
}

/** Parse admin allowlist text: commas, spaces, or newlines. Accepts numeric TG ids. */
export function parsePresaleAllowlist(raw: unknown): string[] {
  const parts: string[] = [];
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (typeof item === 'string' || typeof item === 'number' || typeof item === 'bigint') {
        parts.push(String(item));
      }
    }
  } else if (typeof raw === 'string') {
    parts.push(...raw.split(/[\s,;]+/));
  } else if (typeof raw === 'number' || typeof raw === 'bigint') {
    parts.push(String(raw));
  }
  const out: string[] = [];
  const seen = new Set<string>();
  for (const p of parts) {
    const n = normalizePresaleUsername(p);
    if (n && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  return out;
}

/** Public when `open`, otherwise only allowlisted TG usernames or numeric TG user ids. */
export function canAccessPresale(
  open: boolean,
  allowlist: string[],
  nickname: string | null | undefined,
  tgUserId?: string | number | bigint | null,
): boolean {
  if (open) return true;
  if (allowlist.length === 0) return false;
  const u = normalizePresaleUsername(nickname);
  if (u && allowlist.includes(u)) return true;
  if (tgUserId != null && tgUserId !== '') {
    const id = String(tgUserId);
    if (allowlist.includes(id)) return true;
  }
  return false;
}

/** Pending treasury change awaiting the ops-chat confirmation code. */
export const TREASURY_PENDING_KEY = 'treasury.pending';

/** Pending admin free card issue awaiting the ops-chat confirmation code. */
export const CARD_ADMIN_ISSUE_PENDING_KEY = 'card.admin_issue.pending';

/** Pending admin card withdrawal without a user crypto payout. */
export const CARD_ADMIN_WITHDRAW_PENDING_KEY = 'card.admin_withdraw.pending';
export const CARD_ADMIN_HOLD_PENDING_KEY = 'card.admin_hold.pending';

/** Pending admin link of an already-issued Banana card. */
export const CARD_ADMIN_LINK_PENDING_KEY = 'card.admin_link.pending';

/** Per-template desired stock of pre-issued cards. */
export const CARD_POOL_TARGET_KEY_PREFIX = 'card.pool.target.';
/** Provider failure circuit-breaker; independent of the desired target. */
export const CARD_POOL_PAUSED_KEY_PREFIX = 'card.pool.paused.';

/** Pending admin-user create awaiting the ops-chat confirmation code. */
export const ADMIN_USER_CREATE_PENDING_KEY = 'admin.user_create.pending';

/** Pending admin login awaiting the ops-chat confirmation code. */
export const ADMIN_LOGIN_PENDING_KEY = 'admin.login.pending';

/** One-time admin login ticket after ops-chat code confirmation (consumed by NextAuth). */
export const ADMIN_LOGIN_TICKET_KEY = 'admin.login.ticket';

/** Pending admin INCPT grant awaiting the ops-chat confirmation code. */
export const INCPT_ADMIN_GRANT_PENDING_KEY = 'incpt.admin_grant.pending';

/** Pending admin bulk INCPT grant awaiting the ops-chat confirmation code. */
export const INCPT_ADMIN_BULK_GRANT_PENDING_KEY = 'incpt.admin_bulk_grant.pending';

/** Max recipients in one admin bulk INCPT grant. */
export const INCPT_ADMIN_BULK_GRANT_MAX = 100;

/** Abuse limits for off-chain INCPT P2P transfers (fee is always 0). */
export interface IncptTransferLimits {
  /** Smallest allowed transfer amount (INCPT units). */
  minAmount: number;
  /** Max number of outgoing INCPT transfers per rolling 24h. */
  dailyTxLimit: number;
  /** Minimum seconds between two consecutive outgoing INCPT transfers. */
  cooldownSeconds: number;
}

export const DEFAULT_INCPT_TRANSFER_LIMITS: IncptTransferLimits = {
  minAmount: 0.01,
  dailyTxLimit: 50,
  cooldownSeconds: 5,
};

export const INCPT_TRANSFER_LIMIT_KEYS = {
  minAmount: 'incpt.transfer.min_amount',
  dailyTxLimit: 'incpt.transfer.daily_tx_limit',
  cooldownSeconds: 'incpt.transfer.cooldown_seconds',
} as const;

/** `app_config` keys for the profile Legal link + Support contacts (admin-set). */
export const PROFILE_LINK_KEYS = {
  legal: 'links.legal',
  supportEmail: 'support.email',
  supportTelegram: 'support.telegram',
} as const;

/** `app_config` key holding the FAQ items (admin-managed Q&A accordion). */
export const FAQ_KEY = 'faq.items';

/** Multilingual text (extra langs fall back to EN when empty; UK also falls back to RU). */
export interface FaqText {
  en: string;
  ru: string;
  uk?: string;
  pl?: string;
  es?: string;
  tr?: string;
  el?: string;
  it?: string;
  de?: string;
  pt?: string;
}

export interface FaqItem {
  q: FaqText;
  a: FaqText;
}

export interface ProfileLinks {
  legal: string;
  /** Support email address (used in a mailto link). */
  supportEmail: string;
  /** Support Telegram link (t.me/…) for chatting with an agent. */
  supportTelegram: string;
}

export const DEFAULT_PROFILE_LINKS: ProfileLinks = {
  legal: 'https://telegra.ph/INCPT-Wallet--Terms-of-Service-12-13',
  supportEmail: 'support@incptwallet.com',
  supportTelegram: 'https://t.me/Inception_Support',
};

/** Telegram-bot notification categories the user can toggle on/off. */
export const NOTIFICATION_TYPES = ['deposits', 'transfers', 'cards', 'security'] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];
export type NotificationPrefs = Record<NotificationType, boolean>;

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  deposits: true,
  transfers: true,
  cards: true,
  security: true,
};

/** Merge stored prefs (possibly partial/empty) over the all-on defaults. */
export function mergeNotificationPrefs(stored: unknown): NotificationPrefs {
  const s = (stored ?? {}) as Partial<Record<NotificationType, unknown>>;
  const out = { ...DEFAULT_NOTIFICATION_PREFS };
  for (const k of NOTIFICATION_TYPES) if (typeof s[k] === 'boolean') out[k] = s[k] as boolean;
  return out;
}

export interface TokenInfo {
  /** On-chain contract address (ERC-20 hex / TRC-20 base58). */
  address: string;
  decimals: number;
}

/**
 * Chains that host contract (ERC-20/TRC-20) tokens. Keying TOKENS by this makes
 * `TOKENS[chain]` with a full Chain a compile error, so every consumer has to
 * decide what a coin-only chain means for it.
 *
 * Listed rather than subtracted: `Exclude<Chain, 'btc'>` silently welcomed any
 * chain added later, so Monero would have become a contract chain by default
 * and `TOKENS` would have demanded a stablecoin table for it. A chain joins
 * this list deliberately.
 */
export const CONTRACT_CHAINS = [
  'eth',
  'trx',
  'bsc',
  'sol',
  'hyperevm',
  'ton',
] as const satisfies readonly Chain[];
export type ContractChain = (typeof CONTRACT_CHAINS)[number];

/** Narrows an untrusted or full-union chain to one that has a token table. */
export function isContractChain(chain: Chain): chain is ContractChain {
  return (CONTRACT_CHAINS as readonly Chain[]).includes(chain);
}

/**
 * Mainnet stablecoin contracts, per chain. Decimals differ by chain and are
 * read off the contract before being written down here, never assumed.
 *
 * The inner record is PARTIAL: a chain need not carry both stablecoins. TON is
 * the first that does not — Tether's USDT is the stablecoin there, and no USDC
 * on TON has enough of a presence to accept deposits in. With the record
 * complete, `TOKENS.ton.USDC` typed as present and every caller would have
 * read `undefined.address` at runtime; partial turns that into a compile error
 * at each site instead.
 */
export const TOKENS: Record<ContractChain, Partial<Record<TokenSymbol, TokenInfo>>> = {
  eth: {
    USDT: { address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6 },
    USDC: { address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', decimals: 6 },
  },
  // Binance-Peg USDT/USDC. Note the 18 decimals — unlike every other chain we
  // carry, where both stablecoins are 6. Verified on-chain against symbol()
  // and decimals() before being written down here.
  bsc: {
    USDT: { address: '0x55d398326f99059fF775485246999027B3197955', decimals: 18 },
    USDC: { address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', decimals: 18 },
  },
  // Both read off HyperEVM before being written down, which is how the fake
  // was caught: two contracts there answer symbol() with "USDC", and the one
  // with the more official-looking name ("USD Coin") holds $119 while the
  // Circle-labelled one holds $6.7B. USDT is Tether's omnichain USD₮0 rather
  // than classic USDT — the same kind of substitution we already accept for
  // Binance-Peg USDT on BSC, and the row's network label says which chain it
  // is on.
  hyperevm: {
    USDT: { address: '0xB8CE59FC3717ada4C02eaDF9682A9e934F625ebb', decimals: 6 },
    USDC: { address: '0xb88339CB7199b77E23DB6E890353E22632Ba630f', decimals: 6 },
  },
  // SPL mints. Verified on-chain (owner = SPL Token program, type = mint,
  // decimals 6) and the symbols cross-checked against a token registry, since
  // a mint account carries no name of its own.
  sol: {
    USDT: { address: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB', decimals: 6 },
    USDC: { address: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', decimals: 6 },
  },
  trx: {
    USDT: { address: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t', decimals: 6 },
    USDC: { address: 'TEkxiTehnzSmSe2XqrBj4w32RUN966rdz8', decimals: 6 },
  },
  // TON's stablecoin is a "jetton", not an ERC-20: the balance does not live
  // on the holder's address but in a per-holder contract the master deploys,
  // which is why reading it takes an extra on-chain call.
  //
  // USDT only, and deliberately. Read off mainnet before being written down:
  // 6 decimals, metadata at tether.to/usdt-ton.json, ~1.43B supply — Tether's
  // own jetton. USDC has no equivalent presence on TON, and listing a thinly
  // bridged one would invite deposits we could not price or move.
  ton: {
    USDT: { address: 'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs', decimals: 6 },
  },
};

export interface NativeInfo {
  chain: Chain;
  decimals: number;
}

/** Native chain coins (no contract address; full on-chain precision). */
export const NATIVES: Record<NativeSymbol, NativeInfo> = {
  ETH: { chain: 'eth', decimals: 18 },
  BTC: { chain: 'btc', decimals: 8 },
  TRX: { chain: 'trx', decimals: 6 },
  BNB: { chain: 'bsc', decimals: 18 },
  SOL: { chain: 'sol', decimals: 9 },
  HYPE: { chain: 'hyperevm', decimals: 18 },
  // TON calls its smallest unit a nanoton: 9 decimals, like Solana's lamport
  // and unlike the 18 the EVM chains use.
  TON: { chain: 'ton', decimals: 9 },
  // Transparent ZEC only. Shielded is a different system we neither derive nor
  // spend, so a user's shielded balance is invisible here by construction.
  ZEC: { chain: 'zec', decimals: 8 },
  // Monero's atomic unit is the piconero: 12 decimals, more than any other
  // coin here. A balance that fits an EVM-sized assumption of 18 or a
  // Bitcoin-sized one of 8 is wrong by orders of magnitude in both directions.
  XMR: { chain: 'xmr', decimals: 12 },
  // Dogecoin's smallest unit is the koinu: 8 decimals, same as Bitcoin's
  // satoshi. The resemblance is not a coincidence — Dogecoin forked Litecoin,
  // which forked Bitcoin — but it stops at the number. A koinu is worth far
  // less, which is why the dust limit below is ~1800x Bitcoin's in units.
  DOGE: { chain: 'doge', decimals: 8 },
  // XRP's smallest unit is the drop: 6 decimals, the same as USDT's on most
  // chains. Low precision for a coin, which is why the reserve and the fee —
  // 1 XRP and 10 drops — are both exactly representable here.
  XRP: { chain: 'xrp', decimals: 6 },
};

/** Whether an untrusted string names a chain we support. Query parameters and
 *  request bodies were spelling this out as a chain-by-chain comparison, which
 *  a new chain silently fails: SUPPORTED_CHAINS grows, the parser does not, and
 *  the chain is rejected as invalid with nothing to show why. */
export function isChain(v: unknown): v is Chain {
  return typeof v === 'string' && (SUPPORTED_CHAINS as readonly string[]).includes(v);
}

/** Whether an untrusted string names a token we support (stablecoin or coin). */
export function isAnyToken(v: unknown): v is AnyTokenSymbol {
  return typeof v === 'string' && (ALL_TOKENS as readonly string[]).includes(v);
}

/**
 * Whether an untrusted string names one of our stablecoins.
 *
 * Narrower than `isAnyToken`, which also admits the coins. Routes that take a
 * token in the path and then look it up in `TOKENS` need this one: a coin
 * reaching that lookup has no contract to find.
 */
export function isStablecoin(v: unknown): v is TokenSymbol {
  return typeof v === 'string' && (SUPPORTED_TOKENS as readonly string[]).includes(v);
}

export function isNativeToken(t: string): t is NativeSymbol {
  return (NATIVE_TOKENS as readonly string[]).includes(t);
}

/** The chain's native coin, or null when we don't support it. */
export function nativeForChain(chain: Chain): NativeSymbol | null {
  for (const sym of NATIVE_TOKENS) if (NATIVES[sym].chain === chain) return sym;
  return null;
}

/**
 * Whether this exact chain×token pair exists — the only legal combinations.
 *
 * Both halves ask a table rather than naming chains. Excluding the coin-only
 * chains one by one (`chain !== 'btc'`) would have handed USDT to Monero; and
 * answering `isContractChain(chain)` for any stablecoin, as this did while
 * every contract chain carried both, says TON has USDC. It does not, and the
 * caller would then read an address off `undefined`.
 */
export function isValidChainToken(chain: Chain, token: AnyTokenSymbol): boolean {
  if (isNativeToken(token)) return NATIVES[token].chain === chain;
  return isContractChain(chain) && TOKENS[chain][token] !== undefined;
}

/**
 * The contract for a chain×token pair, or `undefined` when the pair does not
 * exist. The single place that reads `TOKENS` with both keys, so callers get a
 * value they must check instead of indexing twice and trusting the result.
 */
export function tokenInfo(chain: Chain, token: TokenSymbol): TokenInfo | undefined {
  return isContractChain(chain) ? TOKENS[chain][token] : undefined;
}

/**
 * The contract for a pair the caller knows must exist, throwing if it does not.
 *
 * For code that is already on a chain's own path — the Tron adapter, the EVM
 * one — where a missing token is a programming error, not a case to handle.
 * The throw names the pair, so a chain added to `CONTRACT_CHAINS` without the
 * token the path assumes fails with the reason rather than reading `.address`
 * off `undefined`.
 */
export function requireTokenInfo(chain: Chain, token: TokenSymbol): TokenInfo {
  const info = tokenInfo(chain, token);
  if (!info) throw new Error(`chain ${chain} has no ${token}`);
  return info;
}

/** The stablecoins a chain actually carries, in registry order. */
export function tokensForChain(chain: Chain): TokenSymbol[] {
  return isContractChain(chain) ? SUPPORTED_TOKENS.filter((t) => TOKENS[chain][t]) : [];
}

/** Decimals for any legal chain×token combo. Throws on an illegal one so a bad
 *  combo fails loudly instead of silently using the wrong scale. */
export function tokenDecimals(chain: Chain, token: AnyTokenSymbol): number {
  if (!isValidChainToken(chain, token))
    throw new Error(`invalid token ${token} for chain ${chain}`);
  if (isNativeToken(token)) return NATIVES[token].decimals;
  const info = tokenInfo(chain, token);
  // Unreachable while isValidChainToken consults the same table, but the scale
  // is what every amount is multiplied by — so it throws rather than coercing.
  if (!info) throw new Error(`chain ${chain} has no ${token}`);
  return info.decimals;
}

/** BIP coin types for HD derivation: m/purpose'/coin'/0'/0/index. */
export const COIN_TYPE: Record<Chain, number> = {
  eth: 60,
  trx: 195,
  // BNB Smart Chain reuses Ethereum's coin type, so one key serves both and a
  // BSC address IS the user's Ethereum address.
  bsc: 60,
  sol: 501,
  // HyperEVM is an EVM chain, so it reuses Ethereum's coin type and a user's
  // HyperEVM address IS their Ethereum address.
  hyperevm: 60,
  // TON's registered coin type. Our own convention rather than a path to match:
  // native TON wallets derive from their own mnemonic scheme with no path at
  // all, so there is nothing to be compatible with in either direction.
  ton: 607,
  btc: 0,
  // Confirmed against Trezor's coin definition; deriving at Bitcoin's 0 would
  // give valid, spendable addresses in the wrong place relative to every
  // other wallet.
  zec: 133,
  // Monero's registered coin type. The path only picks the BIP-32 node whose
  // private key seeds Monero's own key schedule — from there the derivation
  // leaves BIP-32 entirely.
  xmr: 128,
  // Dogecoin's registered coin type. Deriving at Bitcoin's 0 would produce
  // valid, spendable addresses that no other wallet would ever look at.
  doge: 3,
  // XRP's registered coin type. secp256k1 at this path is what every XRPL
  // wallet that accepts a BIP-39 phrase derives, so the same words show the
  // same account there. (The ledger also allows ed25519 keys, reached by its
  // own seed format rather than by a path — nothing to be compatible with.)
  xrp: 144,
};

/** BIP derivation purpose per chain — BTC uses BIP-84 (native segwit bc1q). */
export const DERIVATION_PURPOSE: Record<Chain, number> = {
  eth: 44,
  trx: 44,
  bsc: 44,
  // SLIP-0010 ed25519, m/44'/501'/i'/0' — the path Phantom and Solflare use,
  // so the same seed phrase shows the same addresses there.
  sol: 44,
  hyperevm: 44,
  // SLIP-0010 ed25519 like Solana, m/44'/607'/i' — TON keys are ed25519, which
  // has no non-hardened derivation, so BIP-32 cannot produce them.
  ton: 44,
  btc: 84,
  // BIP-44, not BIP-84: a transparent address is base58check, not bech32.
  zec: 44,
  // BIP-44 down to the leaf, then Monero's own schedule: the node's private
  // key is reduced mod l to become the spend key, and the view key is the
  // keccak hash of that. Nothing after this point is BIP-32.
  xmr: 44,
  // BIP-44, not BIP-84: Dogecoin has no SegWit, so there is no bech32 address
  // to derive. Every address is base58check P2PKH, like Zcash's transparent.
  doge: 44,
  // BIP-44 all the way down, secp256k1: an XRPL classic address is a
  // base58 encoding of hash160(pubkey), reached the same way Bitcoin's is.
  xrp: 44,
};

export const CHAIN_LABELS: Record<Chain, string> = {
  eth: 'Ethereum',
  trx: 'Tron',
  bsc: 'BNB Smart Chain',
  sol: 'Solana',
  hyperevm: 'HyperEVM',
  ton: 'TON',
  btc: 'Bitcoin',
  zec: 'Zcash',
  xmr: 'Monero',
  doge: 'Dogecoin',
  xrp: 'XRP Ledger',
};
